<template>
    <div
        :class="{ 'is-disabled': switchDisabled, 'is-checked': checked }"
        :aria-checked="checked"
        :aria-disabled="switchDisabled"
        class="app-switch"
        role="switch"
        @click="switchValue">
        <input
            :id="id"
            ref="input"
            :name="name"
            :true-value="activeValue"
            :false-value="inactiveValue"
            :disabled="switchDisabled"
            class="app-switch__input"
            type="checkbox"
            @change="handleChange"
            @keydown.enter="switchValue">
        <span
            v-if="inactiveText"
            :class="['app-switch__label', 'app-switch__label--left', !checked ? 'is-active' : '']">
            <span v-if="inactiveText" :aria-hidden="checked">{{ inactiveText }}</span>
        </span>
        <span ref="core" :style="{ 'width': coreWidth + 'px' }" class="app-switch__core" />
        <span
            v-if="activeText"
            :class="['app-switch__label', 'app-switch__label--right', checked ? 'is-active' : '']">
            <span v-if="activeText" :aria-hidden="!checked">{{ activeText }}</span>
        </span>
    </div>
</template>

<script>
export default {
    name: 'app-switch',
    inject: {
        elForm: {
            default: ''
        }
    },
    props: {
        value: {
            type: [Boolean, String, Number],
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        width: {
            type: Number,
            default: 40
        },
        activeText: String,
        inactiveText: String,
        activeColor: {
            type: String,
            default: ''
        },
        inactiveColor: {
            type: String,
            default: ''
        },
        activeValue: {
            type: [Boolean, String, Number],
            default: true
        },
        inactiveValue: {
            type: [Boolean, String, Number],
            default: false
        },
        name: {
            type: String,
            default: ''
        },
        id: String
    },
    data() {
        return {
            coreWidth: this.width
        }
    },
    computed: {
        checked() {
            return this.value === this.activeValue
        },
        switchDisabled() {
            return this.disabled || (this.elForm || {}).disabled
        }
    },
    created() {
        if (!~[this.activeValue, this.inactiveValue].indexOf(this.value)) {
            this.$emit('input', this.inactiveValue)
        }
    },
    mounted() {
        /* istanbul ignore if */
        this.coreWidth = this.width || 40
        if (this.activeColor || this.inactiveColor) {
            this.setBackgroundColor()
        }
        this.$refs.input.checked = this.checked
    },
    methods: {
        handleChange() {
            const val = this.checked ? this.inactiveValue : this.activeValue
            this.$emit('input', val)
            this.$emit('change', val)
            this.$nextTick(() => {
                // set input's checked property
                // in case parent refuses to change component's value
                this.$refs.input.checked = this.checked
            })
        },
        setBackgroundColor() {
            let newColor = this.checked ? this.activeColor : this.inactiveColor
            this.$refs.core.style.borderColor = newColor
            this.$refs.core.style.backgroundColor = newColor
        },
        switchValue() {
            !this.switchDisabled && this.handleChange()
        },
    },
    watch: {
        checked() {
            this.$refs.input.checked = this.checked
            if (this.activeColor || this.inactiveColor) {
                this.setBackgroundColor()
            }
        }
    },
}
</script>

<style lang="stylus" scoped>
    @import "../../stylus/variables"
    .app-switch
        display: -webkit-inline-box
        display: -ms-inline-flexbox
        display: inline-flex
        -webkit-box-align: center
        -ms-flex-align: center
        align-items: center
        position: relative
        font-size: 14px
        line-height: 20px
        height: 20px
        vertical-align: middle
        &.is-checked
            .app-switch__core
                background: $medium-blue
                border-color: $medium-blue
                &:after
                    left: 100%;
                    margin-left: -17px;

        .app-switch__label
            -webkit-transition: .2s
            -o-transition: .2s
            transition: .2s
            height: 20px
            font-size: 14px
            font-weight: 500
            vertical-align: middle
            color: #303133
            &.is-active
                color: $dark-blue
            
        .app-switch__label--left
            margin-right: 10px
        .app-switch__label--right
            margin-left: 10px

        .app-switch__core,
        .app-switch__label
            display: inline-block
            cursor: pointer

        .app-switch__core
            margin: 0;
            position: relative;
            width: 40px;
            height: 20px;
            border: 1px solid #DCDFE6;
            outline: 0;
            border-radius: 10px;
            -webkit-box-sizing: border-box;
            box-sizing: border-box;
            background: #DCDFE6;
            -webkit-transition: border-color .3s,background-color .3s;
            -o-transition: border-color .3s,background-color .3s;
            transition: border-color .3s,background-color .3s;
            vertical-align: middle;
            &:after
                position: absolute;
                top: 1px;
                left: 1px;
                border-radius: 100%;
                -webkit-transition: all .3s;
                -o-transition: all .3s;
                transition: all .3s;
                width: 16px;
                height: 16px;
                background-color: #FFF;
                content: ""
        
        .app-switch__input 
            position: absolute
            width: 0
            height: 0
            opacity: 0
            margin: 0

</style>
